import React, { useRef } from "react";
import {
  Users,
  CalendarClock,
  Receipt,
  ArrowLeftRight,
  TrendingDown,
  ShieldCheck,
} from "lucide-react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import Reveal from "../common/Reveal";

const EASE = [0.22, 1, 0.36, 1];

function CardShell({ children, className = "", style }) {
  return (
    <div
      className={`eb-card eb-card-hover ${className}`}
      style={{
        padding: "clamp(24px, 2.4vw, 32px)",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function CardHead({ icon: Icon, title, body, accent = "var(--color-primary)" }) {
  return (
    <div>
      <span
        className="eb-icon-tile"
        style={{ background: `${accent}14`, color: accent, marginBottom: "20px" }}
      >
        <Icon size={19} />
      </span>
      <h3
        style={{
          fontFamily: "var(--font-display)",
          fontSize: "1.16rem",
          fontWeight: 700,
          letterSpacing: "-0.02em",
          color: "var(--text-main)",
          marginBottom: "10px",
        }}
      >
        {title}
      </h3>
      <p
        style={{
          color: "var(--text-muted)",
          fontSize: "0.94rem",
          lineHeight: 1.62,
          margin: 0,
        }}
      >
        {body}
      </p>
    </div>
  );
}

/**
 * Customer management → records settle into place one after another, which is
 * what building a directory actually feels like.
 */
function CustomerRows() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const reduceMotion = useReducedMotion();
  const rows = ["Borrower · loan linked", "Borrower · KYC on file", "Borrower · plan active"];

  return (
    <div ref={ref} style={{ display: "grid", gap: "8px", marginTop: "26px" }}>
      {rows.map((label, i) => (
        <motion.div
          key={label}
          initial={reduceMotion ? false : { opacity: 0, y: 10 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, ease: EASE, delay: 0.12 + i * 0.13 }}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            background: "var(--bg-surface)",
            border: "1px solid var(--border-default)",
            borderRadius: "10px",
            padding: "11px 14px",
          }}
        >
          <span
            style={{
              width: "22px",
              height: "22px",
              borderRadius: "50%",
              background: "rgba(37,99,235,0.12)",
              flexShrink: 0,
            }}
          />
          <span
            style={{
              flex: 1,
              height: "7px",
              borderRadius: "4px",
              background: "linear-gradient(90deg, #dbe4f0, #eef2f7)",
            }}
          />
          <span
            style={{
              fontSize: "0.72rem",
              fontWeight: 600,
              color: "var(--text-light)",
              whiteSpace: "nowrap",
            }}
          >
            {label.split(" · ")[1]}
          </span>
        </motion.div>
      ))}
    </div>
  );
}

/**
 * EMI → an installment timeline that draws itself forward.
 */
function EmiTimeline() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const reduceMotion = useReducedMotion();

  return (
    <div ref={ref} style={{ marginTop: "26px" }}>
      <div style={{ position: "relative", height: "3px", background: "var(--border-default)", borderRadius: "2px" }}>
        <motion.div
          initial={reduceMotion ? false : { scaleX: 0 }}
          animate={inView ? { scaleX: 1 } : {}}
          transition={{ duration: 1.1, ease: EASE, delay: 0.15 }}
          style={{
            position: "absolute",
            inset: 0,
            background: "var(--color-indigo)",
            borderRadius: "2px",
            transformOrigin: "left center",
          }}
        />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: "12px" }}>
        {["Month 1", "Month 6", "Month 12"].map((m, i) => (
          <motion.span
            key={m}
            initial={reduceMotion ? false : { opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ duration: 0.4, ease: EASE, delay: 0.35 + i * 0.28 }}
            style={{ fontSize: "0.74rem", fontWeight: 600, color: "var(--text-light)" }}
          >
            {m}
          </motion.span>
        ))}
      </div>
    </div>
  );
}

/**
 * Payments → the receipt confirmation resolves into view.
 */
function ReceiptConfirm() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.6 });
  const reduceMotion = useReducedMotion();

  return (
    <motion.div
      ref={ref}
      initial={reduceMotion ? false : { opacity: 0, scale: 0.96 }}
      animate={inView ? { opacity: 1, scale: 1 } : {}}
      transition={{ duration: 0.6, ease: EASE, delay: 0.2 }}
      style={{
        marginTop: "26px",
        display: "flex",
        alignItems: "center",
        gap: "10px",
        background: "rgba(5,150,105,0.07)",
        border: "1px solid rgba(5,150,105,0.18)",
        borderRadius: "10px",
        padding: "12px 14px",
      }}
    >
      <Receipt size={15} color="var(--color-emerald)" />
      <span style={{ fontSize: "0.82rem", fontWeight: 600, color: "#065f46" }}>
        Receipt issued to the customer
      </span>
    </motion.div>
  );
}

export default function BentoFeatures() {
  return (
    <section id="features" className="eb-bg-white eb-section">
      <div className="container">
        <Reveal className="row" style={{ marginBottom: "clamp(40px, 5vw, 60px)" }}>
          <div className="col-lg-7">
            <span className="eb-eyebrow">Features</span>
            <h2 className="eb-section-title">
              Everything the book has to do,
              <br />
              in one place.
            </h2>
          </div>
        </Reveal>

        <div className="eb-bento">
          <Reveal className="span-7" delay={0.05}>
            <CardShell>
              <CardHead
                icon={Users}
                title="Customer management"
                body="Borrower records, contact details and the loans held against them, searchable from the phone in your pocket."
              />
              <CustomerRows />
            </CardShell>
          </Reveal>

          <Reveal className="span-5" delay={0.12}>
            <CardShell>
              <CardHead
                icon={CalendarClock}
                accent="var(--color-indigo)"
                title="EMI schedules"
                body="Installment plans with clear tenures, so the next due date is never a question."
              />
              <EmiTimeline />
            </CardShell>
          </Reveal>

          <Reveal className="span-5" delay={0.05}>
            <CardShell>
              <CardHead
                icon={Receipt}
                accent="var(--color-purple)"
                title="Payments & receipts"
                body="Log a collection with its amount, transaction number and payment proof attached."
              />
              <ReceiptConfirm />
            </CardShell>
          </Reveal>

          <Reveal className="span-4" delay={0.12}>
            <CardShell>
              <CardHead
                icon={ArrowLeftRight}
                accent="#0891b2"
                title="Transaction history"
                body="Every settled entry stays in the log with its date, value and status — scroll back through the whole book."
              />
            </CardShell>
          </Reveal>

          <Reveal className="span-3" delay={0.18}>
            <CardShell>
              <CardHead
                icon={TrendingDown}
                accent="var(--color-amber)"
                title="Overdue tracking"
                body="See which installments have slipped past their due date, at a glance."
              />
            </CardShell>
          </Reveal>

          <Reveal className="span-12" delay={0.05}>
            <CardShell
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: "clamp(18px, 3vw, 40px)",
                flexWrap: "wrap",
              }}
            >
              <span
                className="eb-icon-tile"
                style={{
                  background: "rgba(5,150,105,0.1)",
                  color: "var(--color-emerald)",
                  flexShrink: 0,
                }}
              >
                <ShieldCheck size={19} />
              </span>
              <div style={{ flex: "1 1 320px" }}>
                <h3
                  style={{
                    fontFamily: "var(--font-display)",
                    fontSize: "1.08rem",
                    fontWeight: 700,
                    letterSpacing: "-0.02em",
                    color: "var(--text-main)",
                    marginBottom: "6px",
                  }}
                >
                  Your book, kept safe
                </h3>
                <p
                  style={{
                    color: "var(--text-muted)",
                    fontSize: "0.94rem",
                    lineHeight: 1.6,
                    margin: 0,
                  }}
                >
                  Every loan, payment and receipt stored against the borrower who holds it —
                  so the record survives long after the paper slip would have.
                </p>
              </div>
            </CardShell>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
